/**
 * dashboard/loadFriendStrip.ts — the /app landing's friends-strip loader.
 *
 * The social-hero dashboard (DashboardHero) renders a "From friends" strip from
 * real friend activity. This helper fetches the FRIENDS feed lane through the
 * same feed client /app/feed uses (`loadFeedLane("friends")`), caps it to a
 * strip-sized slice, and — per Rule 8 — degrades a load failure to an empty list
 * (the hero then shows its calm doorway) rather than throwing or blanking the
 * strip.
 *
 * DASHBOARD-LOCAL freshness cache (deliberately NOT in feed/api.ts): a
 * successful friends-lane read is memoised HERE for a short window so a recipe
 * open → close → landing round-trip (which re-runs the /app effect) does not
 * re-fetch within 30 s. The cache is scoped to the dashboard strip ONLY —
 * `loadFeedLane` and the /app/feed FeedView are untouched, so /app/feed's
 * first-page freshness is unchanged. A landing → /app/feed navigation may
 * re-fetch the first page; that is intentional (not changing the existing feed
 * surface is the priority). Errors are NEVER cached — a failed read retries on
 * the next call. 30 s mirrors recipe.list's staleTime.
 *
 * It is deliberately a small, pure-ish function (not a hook) so the success /
 * cap / error-degrades behaviour is unit-testable without rendering the route.
 * Privacy + entitlement stay server-enforced: this reuses the feed client and
 * invents no endpoint.
 *
 * Refs: branding.md §5 (quiet, honest state), CLAUDE.md Rule 8 (no silent swallow).
 */

import { loadFeedLane, type FeedItem, type FeedLane } from "~/features/feed/api";
import { isNavigationAbort } from "~/lib/nav-abort";

/** The strip is a glance, not the feed: cap the initial page to a sensible row. */
export const FRIEND_STRIP_CAP = 12;

/** Freshness window for the dashboard-local strip cache (mirrors recipe.list). */
const FRIEND_STRIP_STALE_MS = 30_000;

interface CachedStrip {
  /** The full (un-capped) lane items, so a different `cap` still slices right. */
  items: FeedItem[];
  ts: number;
}

let cachedStrip: CachedStrip | null = null;

/** Test-only: drop the dashboard-local strip cache so cases don't cross-contaminate. */
export function __resetFriendStripCache(): void {
  cachedStrip = null;
}

/**
 * Load the friends lane for the dashboard strip. Returns at most `cap` items
 * (newest first, as the lane already returns them). A successful read is served
 * from — and populates — the dashboard-local freshness cache (≤30 s) so a
 * landing round-trip does not re-fetch. A read failure logs and resolves to `[]`
 * (never cached) — the caller renders the doorway; it never rejects.
 *
 * `load` is injectable for tests; it defaults to the real `loadFeedLane`.
 */
export async function loadFriendStrip(
  load: (lane: FeedLane) => Promise<{ items: FeedItem[] }> = loadFeedLane,
  cap: number = FRIEND_STRIP_CAP,
): Promise<FeedItem[]> {
  if (cachedStrip && Date.now() - cachedStrip.ts < FRIEND_STRIP_STALE_MS) {
    return cachedStrip.items.slice(0, cap);
  }
  try {
    const { items } = await load("friends");
    // Only a successful read is cached — a failure below retries next call.
    cachedStrip = { items, ts: Date.now() };
    return items.slice(0, cap);
  } catch (err) {
    // Rule 8: a failed read is logged, never silently swallowed — but it is a
    // read, not a mutation, so it degrades to the calm doorway (no toast). A
    // nav-teardown abort (rapid route change) is expected noise → debug; a
    // genuine failure stays an error.
    if (isNavigationAbort(err)) {
      console.debug({ event: "dashboard_friends_load_aborted", err });
    } else {
      console.error({ event: "dashboard_friends_load_failed", err });
    }
    return [];
  }
}
